import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/lib/api';
import type { UserProfile, AvatarUrlResponse } from '@/types/user';

type ProfileState = {
  // Datos
  profile: UserProfile | null;
  avatarUrl: string; // usar cadena vacía cuando no hay URL

  // Loading
  isLoadingProfile: boolean;
  isLoadingAvatar: boolean;
  isUpdating: boolean;

  error: string | null;

  lastFetchedProfile: number | null;
  lastFetchedAvatar: number | null;

  fetchProfile: () => Promise<void>;
  fetchAvatarUrl: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;

  setLocalAvatar: (url: string) => void; // preview local
  setSignedAvatar: (url: string) => void; // url firmada

  clearError: () => void;
};

const CACHE_MS = 60 * 1000;

export const useProfileStore = create<ProfileState>()(
  persist(
    (set, get) => ({
  profile: null,
  avatarUrl: '',
      isLoadingProfile: false,
      isLoadingAvatar: false,
      isUpdating: false,
      error: null,
      lastFetchedProfile: null,
      lastFetchedAvatar: null,

      fetchProfile: async () => {
        const { lastFetchedProfile } = get();
        if (lastFetchedProfile && Date.now() - lastFetchedProfile < CACHE_MS && get().profile) {
          return;
        }
        set({ isLoadingProfile: true, error: null });
        try {
          const data = await api.user.getProfile();
          set({ profile: data, isLoadingProfile: false, lastFetchedProfile: Date.now() });
        } catch (e: any) {
          set({ isLoadingProfile: false, error: e?.message || 'No se pudo cargar el perfil.' });
        }
      },

      fetchAvatarUrl: async () => {
        const { lastFetchedAvatar } = get();
        if (lastFetchedAvatar && Date.now() - lastFetchedAvatar < CACHE_MS && get().avatarUrl) {
          return;
        }
        set({ isLoadingAvatar: true });
        try {
          // Solicitar URL firmada con duración explícita para reducir expiraciones tempranas
          const resp = (await api.user.getAvatarUrl(3600)) as AvatarUrlResponse & { avatar_path?: string; url?: string };
          let url = (resp as any)?.avatar_url ?? resp?.url ?? '';
          // Detectar URL sin firma (no /sign y sin token)
          const isProbablyUnsigned = typeof url === 'string' && url.includes('/storage/v1/object/') && !url.includes('/storage/v1/object/sign/') && !url.includes('token=');
          // Si a pesar de pedir firma explícita no hay firma, registrar y no actualizar
          if (!url || isProbablyUnsigned) {
            console.warn('[ProfileStore] getAvatarUrl devolvió URL no firmada o inválida incluso con expires_in.');
          }
          // Validar nuevamente después del reintento
          const stillUnsigned = typeof url === 'string' && url.includes('/storage/v1/object/') && !url.includes('/storage/v1/object/sign/') && !url.includes('token=');
          if (!url || stillUnsigned) {
            console.warn('[ProfileStore] URL de avatar sigue sin firma. No se actualizará avatarUrl en el store.', url);
            set({ avatarUrl: '', isLoadingAvatar: false, lastFetchedAvatar: Date.now() });
            return;
          }
          set({ avatarUrl: url, isLoadingAvatar: false, lastFetchedAvatar: Date.now() });
        } catch (e) {
          console.warn('[ProfileStore] getAvatarUrl error', e);
          set({ avatarUrl: '', isLoadingAvatar: false });
        }
      },

      updateProfile: async (updates) => {
        set({ isUpdating: true, error: null });
        try {
          // Mapeo minimal: la API espera solo campos del perfil editables
          await api.user.updateProfile(updates as any);
          const refreshed = await api.user.getProfile();
          set({ profile: refreshed, isUpdating: false, lastFetchedProfile: Date.now() });
        } catch (e: any) {
          set({ isUpdating: false, error: e?.message || 'No se pudo actualizar el perfil.' });
          throw e;
        }
      },

      setLocalAvatar: (url) => set({ avatarUrl: url || '' }),
      setSignedAvatar: (url) => {
        set({ avatarUrl: url || '', lastFetchedAvatar: Date.now() });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'profile-storage',
      partialize: (s) => ({
        profile: s.profile,
        avatarUrl: s.avatarUrl,
        lastFetchedProfile: s.lastFetchedProfile,
        lastFetchedAvatar: s.lastFetchedAvatar,
      }),
      version: 1,
      
    }
  )
);

export const useProfile = () => useProfileStore((s) => s.profile);
export const useAvatarUrl = () => useProfileStore((s) => s.avatarUrl);
export const useProfileLoading = () => useProfileStore((s) => s.isLoadingProfile);
export const useAvatarLoading = () => useProfileStore((s) => s.isLoadingAvatar);
export const useProfileError = () => useProfileStore((s) => s.error);